/**
 * Module-level singleton that tracks in-flight node chat requests.
 *
 * React Flow's `onlyRenderVisibleElements` unmounts nodes that leave the
 * viewport.  The useNodeChat hook lives inside those nodes, so its local
 * state (messages, isLoading) is destroyed on unmount.  This tracker lets
 * the API response handler persist results even when the component is gone,
 * and lets a remounted component subscribe for late-arriving responses.
 */
import type { NodeChatMessage } from './types'

type CompletionCallback = (aiMessage: NodeChatMessage) => void

/** nodeIds with an in-flight chat request */
const activeGenerations = new Set<string>()

/** Callbacks registered by mounted components waiting for a response */
const completionCallbacks = new Map<string, CompletionCallback>()

/** Mark a node as having an in-flight chat request. */
export function markGenerating(nodeId: string): void {
  activeGenerations.add(nodeId)
}

/** Clear the in-flight flag (called when the request completes or is reset). */
export function clearGenerating(nodeId: string): void {
  activeGenerations.delete(nodeId)
}

/** Check whether a node has an in-flight chat request. */
export function isNodeGenerating(nodeId: string): boolean {
  return activeGenerations.has(nodeId)
}

/**
 * Register a callback to be invoked when the in-flight request for `nodeId`
 * completes.  Returns an unsubscribe function.
 *
 * Used by a remounted NodeChat component to pick up a response that arrives
 * after remount but before the old handler writes to node data.
 */
export function onRequestComplete(nodeId: string, cb: CompletionCallback): () => void {
  completionCallbacks.set(nodeId, cb)
  return () => {
    if (completionCallbacks.get(nodeId) === cb) {
      completionCallbacks.delete(nodeId)
    }
  }
}

/**
 * Notify a subscribed (remounted) component that the request completed.
 * Called from the response handler when the originating component has
 * already unmounted.
 */
export function notifyRequestComplete(nodeId: string, aiMessage: NodeChatMessage): void {
  const cb = completionCallbacks.get(nodeId)
  if (cb) {
    cb(aiMessage)
    completionCallbacks.delete(nodeId)
  }
}
